import React, { useState } from 'react';
import { Button, Modal, notification, Space, Spin } from 'antd';
import { useMutation, useQueryClient } from 'react-query';

import {
  getGpsPenaltyImportFields,
  GpsPenaltySchema,
  IGpsPenaltyImport,
  importGps,
  IPenaltyError,
  IPenaltyResult,
  toGpsImportPayload,
} from '@services/penalty';
import { handleAPIError, IFetchQuery } from '@services/base';
import useModal, { IModalPayload } from '@hooks/use-modal';

import Uploader from './components/Uploader';
import Preview from './components/Preview';
import { Import } from 'lucide-react';

/**
 * "Import GPS CSV" (SEN-436): template download, per-row validation against
 * `GpsPenaltySchema`, preview with highlighted errors, import through the
 * `importGps` cloud function and the per-row result report on partial failure.
 */
const ImportCSVPenaltyGpsModal = () => {
  const queryClient = useQueryClient();
  const [modal, setModal] = useModal<IModalPayload<any>>(
    'import-penalty-gps-csv',
  );
  const [, openResultModal] =
    useModal<IModalPayload<IPenaltyResult[]>>('penalty-result');
  const [data, setData] = useState<IGpsPenaltyImport[]>([]);

  const onClose = () => {
    setModal({ isOpen: false, payload: null });
    setData([]);
  };

  const downloadTemplate = () => {
    const headers = getGpsPenaltyImportFields();
    let csvContent = headers.join(',') + '\r\n';
    const emptyLine = Array(headers.length).fill('').join(',') + '\r\n';
    for (let i = 0; i < 100; i++) {
      csvContent += emptyLine;
    }
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'gps-import-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const onParsed = (rows: IGpsPenaltyImport[]) => {
    const newData = rows.map(item => {
      Object.keys(item).forEach(key => {
        const value = item[key as keyof IGpsPenaltyImport];
        if (!value) {
          delete item[key as keyof IGpsPenaltyImport];
        }
      });

      const errors: IPenaltyError[] = [];
      const { error } = GpsPenaltySchema.validate(item, {
        abortEarly: false,
      });
      if (error?.details?.length) {
        error.details.forEach(detail => {
          errors.push({
            message: detail.message,
            column: detail.context?.key || '',
          });
        });
      }

      return {
        ...item,
        errors,
      } as IGpsPenaltyImport;
    });

    setData(newData);
  };

  const { isLoading: isSubmitting, mutate } = useMutation(
    (payload: IFetchQuery[]) => {
      return importGps(payload);
    },
    {
      onSuccess: async res => {
        await queryClient.invalidateQueries('penalties');
        onClose();
        if (res.every(({ success }) => success)) {
          notification.success({
            message: 'Import penalty',
            description: 'Imported successfully',
          });
        } else {
          openResultModal({ isOpen: true, payload: res || [] });
        }
      },
      onError: handleAPIError,
    },
  );

  const submit = () => {
    mutate(data.map(toGpsImportPayload));
  };

  const isValid = !data.some(({ errors }) => errors?.length);

  return (
    <Modal
      destroyOnClose
      centered
      width={720}
      onCancel={onClose}
      footer={null}
      title={
        <>
          <Import size={14} className={'mr-2'} />
          Import GPS Penalties CSV
          {data?.length ? <span className="ml-1">({data.length})</span> : null}
        </>
      }
      visible={modal?.isOpen || false}
    >
      {data?.length ? (
        <Spin spinning={isSubmitting}>
          <div style={{ maxHeight: '280px', overflow: 'hidden' }}>
            <Preview data={data} />
          </div>
          <br />
          <div className={'text-center'}>
            <Space align={'center'}>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                onClick={submit}
                disabled={data.length === 0 || !isValid}
                type="primary"
                icon={<Import size={14} />}
              >
                Import
              </Button>
            </Space>
          </div>
        </Spin>
      ) : (
        <Uploader onParsed={onParsed} onDownloadTemplate={downloadTemplate} />
      )}
    </Modal>
  );
};

export default ImportCSVPenaltyGpsModal;
